/**
 * DevFix Assistant - Compact widget for AI-powered fixes
 */

import { useState, useCallback, useEffect } from 'react';
import { Target, Send, X, Trash2 } from 'lucide-react';
import type { ElementMetadata } from '../../services/devfix/elementInspector';

interface Props {
  selectedElement: ElementMetadata | null;
  onClose: () => void;
  onSelectElement: () => void;
}

interface Task {
  id: string;
  timestamp: string;
  problemDescription: string;
  status: 'pending' | 'in-progress' | 'planned' | 'completed' | 'failed';
  elementMetadata?: ElementMetadata | null;
}

type ViewFilter = 'active' | 'pending' | 'in-progress' | 'planned' | 'completed' | 'failed';

export function DevFixChat({ selectedElement, onClose, onSelectElement }: Props) {
  const [problemInput, setProblemInput] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedView, setSelectedView] = useState<ViewFilter>('active');

  // Fetch tasks periodically
  const fetchTasks = useCallback(async () => {
    try {
      const response = await fetch('/api/devfix/tasks');
      if (response.ok) {
        const data = await response.json();
        setTasks(data.tasks || []);
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    const interval = setInterval(fetchTasks, 3000);
    return () => clearInterval(interval);
  }, [fetchTasks]);

  // Submit new task
  const handleSubmit = useCallback(async () => {
    if (!problemInput.trim()) return;

    const task = {
      id: `task-${Date.now()}`,
      timestamp: new Date().toISOString(),
      elementMetadata: selectedElement,
      problemDescription: problemInput,
      status: 'pending',
    };

    await fetch('/api/devfix/submit-task', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(task),
    });

    setProblemInput('');
    fetchTasks();
  }, [problemInput, selectedElement, fetchTasks]);

  // Delete task
  const handleDeleteTask = useCallback(async (taskId: string, status: string) => {
    try {
      await fetch(`/api/devfix/delete-task/${taskId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      fetchTasks();
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  }, [fetchTasks]);

  // Calculate stats
  const pending = tasks.filter(t => t.status === 'pending').length;
  const inProgress = tasks.filter(t => t.status === 'in-progress').length;
  const planned = tasks.filter(t => t.status === 'planned').length;
  const completed = tasks.filter(t => t.status === 'completed').length;
  const failed = tasks.filter(t => t.status === 'failed').length;

  // Filter tasks based on selected view
  const visibleTasks = tasks.filter(task => {
    if (selectedView === 'active') {
      return task.status === 'pending' || task.status === 'in-progress';
    }
    return task.status === selectedView;
  });

  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      left: '24px',
      width: '400px',
      height: 'calc(100vh - 100px)',
      maxHeight: '800px',
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 99999,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
            AI Fix Assistant
          </h3>
        </div>
        <button
          onClick={onSelectElement}
          style={{
            padding: '8px',
            background: selectedElement ? '#3b82f6' : '#f3f4f6',
            color: selectedElement ? 'white' : '#6b7280',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="Select element"
        >
          <Target size={18} />
        </button>
        <button
          onClick={onClose}
          style={{
            padding: '8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#6b7280',
          }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Status Summary */}
      <div style={{
        padding: '12px 16px',
        background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        gap: '12px',
        fontSize: '13px',
        flexWrap: 'wrap',
      }}>
        <button
          onClick={() => setSelectedView('active')}
          style={{
            background: selectedView === 'active' ? '#e5e7eb' : 'transparent',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            color: '#6b7280',
            fontWeight: selectedView === 'active' ? 600 : 400,
            fontSize: '13px',
          }}
          title="View active tasks (pending + in-progress)"
        >
          Active ({pending + inProgress})
        </button>
        <button
          onClick={() => setSelectedView('pending')}
          style={{
            background: selectedView === 'pending' ? '#fef3c7' : 'transparent',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            color: '#f59e0b',
            fontWeight: selectedView === 'pending' ? 600 : 400,
            fontSize: '13px',
          }}
          title="View pending tasks"
        >
          Pending ({pending})
        </button>
        <button
          onClick={() => setSelectedView('in-progress')}
          style={{
            background: selectedView === 'in-progress' ? '#dbeafe' : 'transparent',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            color: '#3b82f6',
            fontWeight: selectedView === 'in-progress' ? 600 : 400,
            fontSize: '13px',
          }}
          title="View in-progress tasks"
        >
          In Progress ({inProgress})
        </button>
        <button
          onClick={() => setSelectedView('planned')}
          style={{
            background: selectedView === 'planned' ? '#e0e7ff' : 'transparent',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            color: '#6366f1',
            fontWeight: selectedView === 'planned' ? 600 : 400,
            fontSize: '13px',
          }}
          title="View planned tasks (complex, deferred)"
        >
          Planned ({planned})
        </button>
        <button
          onClick={() => setSelectedView('completed')}
          style={{
            background: selectedView === 'completed' ? '#d1fae5' : 'transparent',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            color: '#10b981',
            fontWeight: selectedView === 'completed' ? 600 : 400,
            fontSize: '13px',
          }}
          title="View completed tasks"
        >
          Completed ({completed})
        </button>
        {failed > 0 && (
          <button
            onClick={() => setSelectedView('failed')}
            style={{
              background: selectedView === 'failed' ? '#fee2e2' : 'transparent',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              color: '#ef4444',
              fontWeight: selectedView === 'failed' ? 600 : 400,
              fontSize: '13px',
            }}
            title="View failed tasks"
          >
            Failed ({failed})
          </button>
        )}
      </div>

      {/* Task List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px',
      }}>
        {visibleTasks.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#9ca3af',
            padding: '40px 20px',
            fontSize: '14px',
          }}>
            {selectedView === 'active' && 'No active tasks'}
            {selectedView === 'pending' && 'No pending tasks'}
            {selectedView === 'in-progress' && 'No tasks in progress'}
            {selectedView === 'planned' && 'No planned tasks'}
            {selectedView === 'completed' && 'No completed tasks'}
            {selectedView === 'failed' && 'No failed tasks'}
          </div>
        ) : (
          <>
            {/* Task Items */}
            {visibleTasks.map((task) => {
              const isPending = task.status === 'pending';
              const isProcessing = task.status === 'in-progress';
              const isPlanned = task.status === 'planned';
              const isCompleted = task.status === 'completed';
              const isFailed = task.status === 'failed';

              let backgroundColor = '#fff';
              let borderColor = '#e5e7eb';

              if (isProcessing) {
                backgroundColor = '#dbeafe';
                borderColor = '#3b82f6';
              } else if (isPlanned) {
                backgroundColor = '#eef2ff';
                borderColor = '#6366f1';
              } else if (isCompleted) {
                backgroundColor = '#f0fdf4';
                borderColor = '#10b981';
              } else if (isFailed) {
                backgroundColor = '#fef2f2';
                borderColor = '#ef4444';
              }

              return (
                <div
                  key={task.id}
                  style={{
                    marginBottom: '8px',
                    padding: '12px',
                    background: backgroundColor,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                  }}
                >
                  <div style={{
                    fontSize: '13px',
                    color: '#374151',
                    marginBottom: '8px',
                    lineHeight: '1.4',
                  }}>
                    {task.problemDescription}
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '6px',
                    alignItems: 'center',
                  }}>
                    <span style={{
                      fontSize: '11px',
                      color: '#9ca3af',
                      flex: 1,
                    }}>
                      {new Date(task.timestamp).toLocaleTimeString()}
                    </span>
                    <button
                      onClick={() => handleDeleteTask(task.id, task.status)}
                      style={{
                        padding: '6px',
                        background: '#fee2e2',
                        color: '#ef4444',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      title="Delete task"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Input Area */}
      <div style={{
        padding: '12px',
        borderTop: '1px solid #e5e7eb',
        background: '#f9fafb',
      }}>
        {selectedElement && (
          <div style={{
            marginBottom: '8px',
            padding: '10px',
            background: '#dbeafe',
            borderRadius: '6px',
            fontSize: '11px',
            color: '#1e40af',
            maxHeight: '200px',
            overflowY: 'auto',
          }}>
            <div style={{ fontWeight: 600, marginBottom: '6px', fontSize: '12px' }}>
              Selected Element
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>Component:</strong> {selectedElement.componentName || 'Unknown'}
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>URL:</strong> {window.location.href}
            </div>
            <div style={{ marginBottom: '4px' }}>
              <strong>DOM Path:</strong> {selectedElement.domPath}
            </div>
            {selectedElement.boundingBox && (
              <div style={{ marginBottom: '4px' }}>
                <strong>Position:</strong> x:{Math.round(selectedElement.boundingBox.x)},
                y:{Math.round(selectedElement.boundingBox.y)},
                w:{Math.round(selectedElement.boundingBox.width)},
                h:{Math.round(selectedElement.boundingBox.height)}
              </div>
            )}
            {selectedElement.innerHTML && (
              <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid #93c5fd' }}>
                <strong>Preview:</strong>
                <div style={{
                  marginTop: '4px',
                  fontSize: '10px',
                  color: '#1e40af',
                  opacity: 0.8,
                  maxHeight: '60px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {selectedElement.innerHTML.slice(0, 200)}...
                </div>
              </div>
            )}
          </div>
        )}
        <div style={{ position: 'relative' }}>
          <textarea
            value={problemInput}
            onChange={(e) => setProblemInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Describe the problem or request..."
            style={{
              width: '100%',
              padding: '12px',
              paddingRight: '50px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              resize: 'none',
              minHeight: '80px',
              fontFamily: 'inherit',
            }}
          />
          <button
            onClick={handleSubmit}
            disabled={!problemInput.trim()}
            style={{
              position: 'absolute',
              bottom: '12px',
              right: '12px',
              padding: '8px 12px',
              background: problemInput.trim() ? '#3b82f6' : '#e5e7eb',
              color: problemInput.trim() ? 'white' : '#9ca3af',
              border: 'none',
              borderRadius: '6px',
              cursor: problemInput.trim() ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
